import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import * as _ from 'lodash-es';

@Injectable({
  providedIn: 'root'
})
export class EcQuartermapSetupService {

  constructor(private http: HttpClient) { }

  /**
   * Legacy create: POST saveJsonObject?action_code=411nrd
   * (ecQuarterMapCreateActionsController.save → JsonObjectFactory.saveJSON).
   */
  saveQuarterMapForm(
    baseUrl: string,
    formData: any,
    globalService: any,
    filterParams: any,
    rowData: any
  ): Observable<any> {
    const url = `${baseUrl}/saveJsonObject?action_code=411nrd`;
    const globalParams = globalService?.globalParams || {};
    const taxYear = filterParams?.tax_year ?? formData?.TAX_YEAR ?? rowData?.TAX_YEAR;

    const jsonObj = _.merge({}, formData, filterParams || {});
    const params = _.merge({}, globalParams, filterParams || {});
    params.json_data = JSON.stringify(jsonObj);
    params.jsonSettings = JSON.stringify({ tax_year: taxYear });
    params.scenarioCode = formData?.SCENARIO_TYPE_CODE;
    params.is_issue_key_enabled = 'N';
    if (taxYear != null && taxYear !== '') {
      params.tax_year = taxYear;
    }
    this.applyLockPayload(params, filterParams, rowData, globalParams);

    return this.postForm(url, params);
  }

  /**
   * Legacy delete: POST saveJsonObject?action_code=wo1977
   * (ecQuarterMapUpdateActionsController.delete).
   */
  deleteQuarterMap(
    baseUrl: string,
    grpObjKey: string,
    filingCycle: string,
    rptMth: string,
    globalService: any,
    filterParams: any,
    rowData: any
  ): Observable<any> {
    const url = `${baseUrl}/saveJsonObject?action_code=wo1977`;
    const globalParams = globalService?.globalParams || {};
    const params = _.merge({}, globalParams, filterParams || {}, {
      grp_obj_key: grpObjKey,
      filing_cycle: filingCycle,
      rpt_mth: rptMth,
      is_issue_key_enabled: 'N'
    });
    const taxYear = filterParams?.tax_year ?? rowData?.TAX_YEAR;
    if (taxYear != null && taxYear !== '') {
      params.tax_year = taxYear;
    }
    this.applyLockPayload(params, filterParams, rowData, globalParams);

    return this.postForm(url, params);
  }

  private postForm(url: string, params: any): Observable<any> {
    const body = new URLSearchParams();
    Object.keys(params || {}).forEach(key => {
      const value = params[key];
      if (value !== null && value !== undefined && value !== '') {
        body.set(key, typeof value === 'string' ? value : String(value));
      }
    });

    return this.http.post<any>(url, body.toString(), {
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded'
      }),
      withCredentials: true
    });
  }

  private applyLockPayload(params: any, filterParams: any, rowData: any, globalParams: any): void {
    const lock: any = {};
    if (rowData?.LEID) {
      lock.leid = rowData.LEID;
    }
    if (rowData?.CDR_NO) {
      lock.cdr_number = rowData.CDR_NO;
    }
    if (rowData?.CONSOL_GROUP_KEY != null && rowData.CONSOL_GROUP_KEY !== '') {
      lock.consol_group_key = String(rowData.CONSOL_GROUP_KEY);
    }
    if (rowData?.REPORTING_PERIOD != null && rowData.REPORTING_PERIOD !== '') {
      lock.reporting_period = String(rowData.REPORTING_PERIOD);
    }
    if (rowData?.COMBINATION_KEY != null && rowData.COMBINATION_KEY !== '') {
      lock.combination_key = String(rowData.COMBINATION_KEY);
    }
    const scenarioKey = filterParams?.scenario ?? globalParams?.scenario ?? rowData?.SCENARIO;
    if (scenarioKey != null && scenarioKey !== '' && !Number.isNaN(parseInt(scenarioKey, 10))) {
      lock.scenario_key = parseInt(scenarioKey, 10);
    }
    if (Object.keys(lock).length > 0) {
      params.checkLockedbj = JSON.stringify([lock]);
    }
  }

  loadScenarioTypeCode(baseUrl: string, filterParams: any, rowData: any = {}): Observable<any[]> {
    const baseParams: any = {
      action_id: 32735,
      action_code: '73pnl0',
      activity_key: filterParams?.activity_key || rowData.ACTIVITY_KEY || 5124,
      is_locked: 'N',
      issue_id: '',
      issue_id_for_tx_save: '',
      issue_short_desc: '',
      jcd_key: rowData.JCD_KEY || 250,
      jcd_ta_key: filterParams?.jcd_ta_key || rowData.JCD_TA_KEY || 5133,
      scenario: filterParams?.scenario || rowData.SCENARIO || 74,
      screen_key: filterParams?.screen_key || rowData.SCREEN_KEY || 11451,
      screen_type: 'grid',
      tax_year: filterParams?.tax_year ?? rowData?.TAX_YEAR,
      scenario_type_code: null,
      scenario_desc: null
    };

    // 🚀 CRITICAL: Build URL with query parameters for GET request
    const queryParams = new URLSearchParams();
    Object.keys(baseParams).forEach(key => {
      if (baseParams[key] !== null && baseParams[key] !== undefined) {
        queryParams.set(key, baseParams[key]);
      }
    });

    const url = `${baseUrl}/loadJsonObject?${queryParams.toString()}`;

    return this.http.get<any>(url).pipe(
      // Map response to extract jsonObject array
      map((response: any) => {
        if (response.callSuccess === "1") {
          return response.jsonObject || [];
        } else {
          throw new Error(response.errorMessage || 'Failed to load scenario type codes');
        }
      })
    );
  }

  loadScenarioDesc(baseUrl: string, selectedValue: string, filterParams: any, rowData: any = {}): Observable<any[]> {
    const baseParams: any = {
      action_id: 32735,
      action_code: '73pnl0',
      activity_key: filterParams?.activity_key || rowData.ACTIVITY_KEY || 5124,
      is_locked: 'N',
      issue_id: '',
      issue_id_for_tx_save: '',
      issue_short_desc: '',
      jcd_key: rowData.JCD_KEY || 250,
      jcd_ta_key: filterParams?.jcd_ta_key || rowData.JCD_TA_KEY || 5133,
      scenario: filterParams?.scenario || rowData.SCENARIO || 74,
      screen_key: filterParams?.screen_key || rowData.SCREEN_KEY || 11451,
      screen_type: 'grid',
      tax_year: filterParams?.tax_year ?? rowData?.TAX_YEAR,
      scenario_type_code: selectedValue,
      scenario_desc: null
    };

    // 🚀 CRITICAL: Build URL with query parameters for GET request
    const queryParams = new URLSearchParams();
    Object.keys(baseParams).forEach(key => {
      if (baseParams[key] !== null && baseParams[key] !== undefined) {
        queryParams.set(key, baseParams[key]);
      }
    });

    const url = `${baseUrl}/loadJsonObject?${queryParams.toString()}`;

    return this.http.get<any>(url).pipe(
      // Map response to extract jsonObject array
      map((response: any) => {
        if (response.callSuccess === "1") {
          return response.jsonObject || [];
        } else {
          throw new Error(response.errorMessage || 'Failed to load scenario descriptions');
        }
      })
    );
  }

  loadGroupObjKey(baseUrl: string, selectedVal1: string, selectedVal2: string, filterParams: any, rowData: any = {}): Observable<any[]> {
    const baseParams: any = {
      action_id: 32735,
      action_code: '73pnl0',
      activity_key: filterParams?.activity_key || rowData.ACTIVITY_KEY || 5124,
      is_locked: 'N',
      issue_id: '',
      issue_id_for_tx_save: '',
      issue_short_desc: '',
      jcd_key: rowData.JCD_KEY || 250,
      jcd_ta_key: filterParams?.jcd_ta_key || rowData.JCD_TA_KEY || 5133,
      scenario: filterParams?.scenario || rowData.SCENARIO || 74,
      screen_key: filterParams?.screen_key || rowData.SCREEN_KEY || 11451,
      screen_type: 'grid',
      tax_year: filterParams?.tax_year ?? rowData?.TAX_YEAR,
      scenario_type_code: selectedVal1,
      scenario_desc: selectedVal2
    };

    // 🚀 CRITICAL: Build URL with query parameters for GET request
    const queryParams = new URLSearchParams();
    Object.keys(baseParams).forEach(key => {
      if (baseParams[key] !== null && baseParams[key] !== undefined) {
        queryParams.set(key, baseParams[key]);
      }
    });

    const url = `${baseUrl}/loadJsonObject?${queryParams.toString()}`;

    return this.http.get<any>(url).pipe(
      // Map response to extract jsonObject array
      map((response: any) => {
        if (response.callSuccess === "1") {
          return response.jsonObject || [];
        } else {
          throw new Error(response.errorMessage || 'Failed to load group object keys');
        }
      })
    );
  }

  loadDistFilingCycle(baseUrl: string, filterParams: any, rowData: any = {}): Observable<any[]> {
    const baseParams: any = {
      action_id: 32814,
      action_code: 'gjav79',
      activity_key: filterParams?.activity_key || rowData.ACTIVITY_KEY || 5124,
      is_locked: 'N',
      issue_id: '',
      issue_id_for_tx_save: '',
      issue_short_desc: '',
      jcd_key: rowData.JCD_KEY || 250,
      jcd_ta_key: filterParams?.jcd_ta_key || rowData.JCD_TA_KEY || 5133,
      scenario: filterParams?.scenario || rowData.SCENARIO || 74,
      screen_key: filterParams?.screen_key || rowData.SCREEN_KEY || 11451,
      screen_type: 'grid',
      tax_year: filterParams?.tax_year ?? rowData?.TAX_YEAR
    };

    // 🚀 CRITICAL: Build URL with query parameters for GET request
    const queryParams = new URLSearchParams();
    Object.keys(baseParams).forEach(key => {
      if (baseParams[key] !== null && baseParams[key] !== undefined) {
        queryParams.set(key, baseParams[key]);
      }
    });

    const url = `${baseUrl}/loadJsonObject?${queryParams.toString()}`;

    return this.http.get<any>(url).pipe(
      // Map response to extract jsonObject array
      map((response: any) => {
        if (response.callSuccess === "1") {
          return response.jsonObject || [];
        } else {
          throw new Error(response.errorMessage || 'Failed to load filing cycles');
        }
      })
    );
  }
}
